<template>
  <div class="mx-auto max-w-2xl space-y-6">
    <div class="text-center">
      <p class="text-navy-500 text-sm">
        Importing from
        <span class="text-navy-800 font-semibold">{{
          agent.name || "Agent"
        }}</span>
      </p>
    </div>

    <!-- File Number Card -->
    <AppCard title="File Number" subtitle="Enter the file number to look up">
      <form class="space-y-4" @submit.prevent="fetchOrder">
        <div>
          <label
            for="file-number-input"
            class="text-navy-700 mb-1.5 block text-sm font-medium"
          >
            File Number
          </label>
          <input
            id="file-number-input"
            v-model="fileNumber"
            type="text"
            placeholder="e.g. ORD-24-0001247"
            :disabled="isFetching"
            class="text-navy-800 placeholder:text-navy-300 w-full rounded-md border border-neutral-300 px-3 py-2 font-mono text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none disabled:opacity-50"
          />
          <p v-if="fetchError" class="mt-1.5 text-xs text-red-600">
            {{ fetchError }}
          </p>
        </div>

        <AppButton
          type="submit"
          :disabled="!fileNumber.trim() || isFetching"
          :loading="isFetching"
        >
          {{ isFetching ? "Fetching..." : "Fetch Order" }}
        </AppButton>
      </form>
    </AppCard>

    <!-- Loading State -->
    <div v-if="isFetching" class="flex items-center justify-center gap-3 py-8">
      <i class="pi pi-spin pi-spinner text-navy-400 text-lg" />
      <span class="text-navy-500 text-sm">
        Fetching order from {{ agent.name || "agent" }}...
      </span>
    </div>

    <!-- Order Preview Card -->
    <AppCard v-if="fetchedOrder" title="Order Found" accent="bg-green-500">
      <div class="grid grid-cols-2 gap-x-8 gap-y-4">
        <div class="col-span-2">
          <dt class="text-navy-400 text-xs font-medium tracking-wide uppercase">
            Property
          </dt>
          <dd class="text-navy-800 mt-1 text-sm font-medium">
            {{ formatAddress(fetchedOrder) }}
          </dd>
        </div>
        <div>
          <dt class="text-navy-400 text-xs font-medium tracking-wide uppercase">
            Closing Date
          </dt>
          <dd class="text-navy-800 mt-1 text-sm">
            {{ formatDate(fetchedOrder.ClosingDate) }}
          </dd>
        </div>
        <div>
          <dt class="text-navy-400 text-xs font-medium tracking-wide uppercase">
            Type
          </dt>
          <dd class="text-navy-800 mt-1 text-sm">
            {{
              [fetchedOrder.TransactionType, fetchedOrder.SettlementType]
                .filter(Boolean)
                .join(" \u00B7 ") || "\u2014"
            }}
          </dd>
        </div>
        <div>
          <dt class="text-navy-400 text-xs font-medium tracking-wide uppercase">
            County
          </dt>
          <dd class="text-navy-800 mt-1 text-sm">
            {{ fetchedOrder.Property?.County || "\u2014" }}
          </dd>
        </div>
        <div>
          <dt class="text-navy-400 text-xs font-medium tracking-wide uppercase">
            Buyer(s)
          </dt>
          <dd class="text-navy-800 mt-1 text-sm">
            {{ partySummary(fetchedOrder, "Buyer") }}
          </dd>
        </div>
        <div>
          <dt class="text-navy-400 text-xs font-medium tracking-wide uppercase">
            Seller(s)
          </dt>
          <dd class="text-navy-800 mt-1 text-sm">
            {{ partySummary(fetchedOrder, "Seller") }}
          </dd>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-between">
          <p class="flex items-center gap-1.5 text-xs text-green-600">
            <i class="pi pi-check-circle" />
            This is the right order
          </p>
          <AppButton @click="$emit('continue', fetchedOrder)">
            Continue
          </AppButton>
        </div>
      </template>
    </AppCard>

    <!-- Back link -->
    <div class="text-center">
      <button
        class="text-navy-500 hover:text-navy-700 cursor-pointer text-sm transition-colors"
        :disabled="isFetching"
        @click="$emit('back')"
      >
        &larr; Back to source selection
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onUnmounted, ref } from "vue"

import type { ConnectedAgent, FetchedOrderData } from "../../pages/Order/types"
import DISPATCH_FETCH_ORDER from "../../queries/agent_registration/dispatchFetchOrder.gql"
import POLL_WORK_ITEM from "../../queries/agent_work_item/poll.gql"
import { apolloClient } from "../../queries/client"
import AppButton from "../ui/AppButton.vue"
import AppCard from "../ui/AppCard.vue"

const props = defineProps<{
  agent: ConnectedAgent
}>()

defineEmits<{
  back: []
  continue: [data: FetchedOrderData]
}>()

const fileNumber = ref("")
const isFetching = ref(false)
const fetchError = ref<string | undefined>()
const fetchedOrder = ref<FetchedOrderData | null>(null)
let pollInterval: ReturnType<typeof setInterval> | null = null

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
})

const formatAddress = (order: FetchedOrderData): string => {
  const p = order.Property
  if (!p) return "\u2014"
  const parts = [p.Street]
  const cityState = [p.City, p.State].filter(Boolean).join(", ")
  if (cityState) parts.push(cityState)
  if (p.ZipCode) parts[parts.length - 1] += ` ${p.ZipCode}`
  return parts.filter(Boolean).join(", ") || "\u2014"
}

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "\u2014"
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  } catch {
    return dateStr
  }
}

const partySummary = (order: FetchedOrderData, role: string): string => {
  const parties = (order.Parties || [])
    .filter((p) => p.Role === role)
    .sort((a, b) => a.Index - b.Index)
    .map((p) => p.FullName)
  return parties.length > 0 ? parties.join(", ") : "\u2014"
}

const fetchOrder = async () => {
  if (!fileNumber.value.trim()) return

  isFetching.value = true
  fetchError.value = undefined
  fetchedOrder.value = null

  try {
    const result = await apolloClient.mutate({
      mutation: DISPATCH_FETCH_ORDER,
      variables: {
        agentRegistrationId: props.agent.id,
        fileNumber: fileNumber.value.trim(),
      },
    })

    if (result?.data?.dispatchFetchOrder?.errors?.length) {
      fetchError.value = result.data.dispatchFetchOrder.errors.join(", ")
      isFetching.value = false
      return
    }

    const workItemId = result.data.dispatchFetchOrder.workItem.id
    pollForResult(workItemId)
  } catch (error) {
    console.error("Dispatch error:", error)
    fetchError.value = "Failed to dispatch fetch request"
    isFetching.value = false
  }
}

const pollOnce = async (workItemId: string) => {
  try {
    const result = await apolloClient.query({
      query: POLL_WORK_ITEM,
      variables: { id: workItemId },
      fetchPolicy: "network-only",
    })

    const item = result.data.agentWorkItem
    if (!item) return

    if (item.status === "completed") {
      clearInterval(pollInterval!)
      pollInterval = null
      isFetching.value = false
      fetchedOrder.value = item.result as FetchedOrderData
    } else if (item.status === "failed") {
      clearInterval(pollInterval!)
      pollInterval = null
      isFetching.value = false
      fetchError.value = item.errorMessage || "Order not found"
    }
  } catch (error) {
    console.error("Poll error:", error)
    clearInterval(pollInterval!)
    pollInterval = null
    isFetching.value = false
    fetchError.value = "Lost connection while fetching order"
  }
}

const pollForResult = (workItemId: string) => {
  if (pollInterval) clearInterval(pollInterval)

  pollInterval = setInterval(() => {
    void pollOnce(workItemId)
  }, 2000)
}
</script>
